import { type SelectionsMachine } from '../../machine/selection-machine.ts';
import { hasActiveEdit } from '../../machine/utils/has-active-edit.ts';
import { type MouseInputAdapter } from '../mouse-input/mouse-input-adapter.ts';
import { type SelectionUiInteraction } from '../selection-ui-interaction.ts';
import { type SelectionInput } from '../types.ts';

export interface CreateContextMenuInputOptions {
  /**
   * When set, emits a `contextMenu` selection UI interaction immediately before
   * dispatching `selectCell`. `startTime` is captured at the very top of the
   * `contextmenu` handler so duration covers target resolution, dispatch, and
   * render to "menu visible".
   */
  onSelectionUiInteraction?: (event: SelectionUiInteraction) => void;
}

/**
 * Converts a cell `contextmenu` interaction into the existing `selectCell`
 * transition, including pointer coordinates used by the context-menu effect.
 *
 * This input owns the document listener and removes it on disposal. It ignores
 * active editing and delegates target resolution to the injected mouse adapter.
 */
export function createContextMenuInput(
  machine: SelectionsMachine,
  hitTest: MouseInputAdapter['hitTest'],
  options: CreateContextMenuInputOptions = {}
): SelectionInput {
  const { onSelectionUiInteraction } = options;

  function onContextMenu(event: PointerEvent) {
    const startTime = performance.now();
    const state = machine.getState();

    // ignore if we're currently editing.
    if (hasActiveEdit(state)) {
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const target = hitTest(x, y);

    if (target.type === 'cell') {
      event.preventDefault();

      onSelectionUiInteraction?.({
        source: 'contextMenu',
        startTime,
        detail: {
          rowIndex: target.position.row.index,
          colIndex: target.position.col.index
        }
      });

      machine.dispatch({
        type: 'selectCell',
        cell: target.position,
        source: 'mouse',
        contextMenuTrigger: { x, y }
      });
    }
  }

  return {
    setup() {
      document.addEventListener('contextmenu', onContextMenu);
    },

    dispose() {
      document.removeEventListener('contextmenu', onContextMenu);
    }
  };
}
